import CourseCard from './CourseCard.js';

/**
 * Filter voor het opleidingenoverzicht. Alle kaarten staan al in de pagina, per
 * select wordt er enkel getoond en verborgen. De keuze gaat ook in de url, zodat
 * een gefilterd overzicht te delen is en de pagina er direct goed in komt.
 */
export default class Courses {
	constructor(element) {
		this.element = element;
		this.selects = Array.from(element.querySelectorAll('[data-course-filter]'));
		this.cards = Array.from(element.querySelectorAll('[data-course-card]'));
		// De kaarten zorgen zelf voor hun video, het filteren raakt ze verder
		// niet: een verborgen kaart komt niet in beeld en speelt dus ook niet
		this.courseCards = this.cards.map((card) => new CourseCard(card));
		this.ctaCard = element.querySelector('[data-cta-card]');
		this.empty = element.querySelector('[data-courses-empty]');
		this.count = element.querySelector('[data-courses-count]');

		this.onChange = this.onChange.bind(this);
		this.onPopState = this.onPopState.bind(this);
		this.onCardPlay = this.onCardPlay.bind(this);

		this.selects.forEach((select) => {
			select.addEventListener('change', this.onChange);
		});

		this.element.addEventListener('course-card-play', this.onCardPlay);

		window.addEventListener('popstate', this.onPopState);
	}

	/**
	 * De gekozen filters als { filter: waarde }, lege selects tellen niet mee
	 */
	getActiveFilters() {
		return this.selects.reduce((filters, select) => {
			if (select.value) {
				filters[select.dataset.courseFilter] = select.value;
			}

			return filters;
		}, {});
	}

	/**
	 * Een kaart blijft staan als hij bij elk actief filter een passende waarde
	 * heeft (een term of een startdatum). Een opleiding kan er meerdere hebben,
	 * vandaar het splitsen.
	 */
	matches(card, filters) {
		return Object.entries(filters).every(([key, value]) => {
			const values = (card.getAttribute(`data-${key}`) || '').split(' ');

			return values.includes(value);
		});
	}

	filter() {
		const filters = this.getActiveFilters();
		let visible = 0;

		this.cards.forEach((card) => {
			const matches = this.matches(card, filters);

			card.classList.toggle('is-hidden', !matches);

			if (matches) {
				visible++;
			}
		});

		// De cta-kaart hangt aan zijn plek in de rij, en die plek zegt niets meer
		// zodra er gefilterd is
		if (this.ctaCard) {
			this.ctaCard.classList.toggle('is-hidden', Object.keys(filters).length > 0);
		}

		if (this.empty) {
			this.empty.hidden = visible > 0;
		}

		if (this.count) {
			this.count.textContent = `${visible} opleidingen gevonden`;
		}
	}

	/**
	 * replaceState en niet pushState: het filteren is geen nieuwe pagina, de
	 * terugknop hoort naar de vorige pagina te gaan en niet door de filters heen
	 */
	updateUrl() {
		const params = new URLSearchParams(window.location.search);

		this.selects.forEach((select) => {
			const key = select.dataset.courseFilter;

			if (select.value) {
				params.set(key, select.value);
			} else {
				params.delete(key);
			}
		});

		const query = params.toString();

		window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
	}

	/**
	 * Een aangeraakte kaart begint te spelen (zie CourseCard.js), de rest houdt
	 * ermee op: op tablet staan er meerdere kaarten tegelijk in beeld
	 *
	 * @param {CustomEvent} e
	 */
	onCardPlay(e) {
		this.courseCards.forEach((card) => {
			if (card.element !== e.target) {
				card.stop();
			}
		});
	}

	onChange() {
		this.filter();
		this.updateUrl();
		this.element.dispatchEvent(new CustomEvent('refresh-scroll', { bubbles: true }));
	}

	/**
	 * Komt de bezoeker terug op een gefilterde url, dan moeten de selects weer
	 * gelijklopen met wat er in de url staat
	 */
	onPopState() {
		const params = new URLSearchParams(window.location.search);

		this.selects.forEach((select) => {
			const value = params.get(select.dataset.courseFilter) || '';
			const exists = Array.from(select.options).some((option) => option.value === value);

			select.value = exists ? value : '';
		});

		this.filter();
	}

	destroy() {
		this.courseCards.forEach((card) => card.destroy());

		this.element.removeEventListener('course-card-play', this.onCardPlay);

		this.selects.forEach((select) => {
			select.removeEventListener('change', this.onChange);
		});

		window.removeEventListener('popstate', this.onPopState);
	}
}
